import {CustomNodeData} from '../tabs/ReactFlowTab'
import {
    type Edge,
    type Node,
} from '@xyflow/react';

import { base64ToArrayBuffer } from '../RepresentationSelectorModal';
import { RectAreaLightUniformsLib, STLLoader } from 'three-stdlib';
import { LoadedModel } from '../tabs/ThreeDTab';
import * as THREE from 'three';
import { v4 as uuidv4 } from 'uuid';


/**
 * Creates the component node and loads the 3D component 
 * @param {string}componentText 
 * @param {React.Dispatch<React.SetStateAction<Node<CustomNodeData>[]>>}  setNodes 
 * @param {React.Dispatch<React.SetStateAction<LoadedModel[]>>} setLoadedModels
 */
export function compile_simulation(componentText: string,
    setNodes: React.Dispatch<React.SetStateAction<Node<CustomNodeData>[]>>,
    setLoadedModels: React.Dispatch<React.SetStateAction<LoadedModel[]>>,
    setPlots: React.Dispatch<React.SetStateAction<Map<string, any>>>,
    setTable: React.Dispatch<React.SetStateAction<Map<string, any>>>,
    ) {

    console.log('Creating representation with data:', componentText);
    

    // Remove all models with IDs starting with 'model_beam_'
    setLoadedModels((prevModels) => prevModels.filter(model => !model.id.startsWith('model_beam')));

    let response;
    try {
        componentText = base64ToUtf8(componentText);
        response = JSON.parse(componentText);
    } catch (error) {
        console.error('Failed to parse JSON:', error);
        console.error('Invalid JSON content:', componentText);
        return; // Exit the function if JSON is invalid
    }

    for (const element of response) {
        console.log('Processing element:', element);
        // Add your processing logic here

        switch (element.type) {
            case "stl_beam":
                // This is a beam element
                let stl = element.stl.replace(/\r\n/g, '\n').replace(/[^\x20-\x7E\n]/g, '');
                let color = element.lambda;

                // Create a new model object
                const loader = new STLLoader();
                const blob = new Blob([stl], { type: 'text/plain' });
                const url = URL.createObjectURL(blob);

                loader.load(url, (geometry) => {
                    const material = new THREE.MeshStandardMaterial({ color, side: THREE.DoubleSide });
                    const mesh = new THREE.Mesh(geometry, material);
                    mesh.scale.set(0.100, 0.100, 0.100);
                    setLoadedModels((prevModels) => [
                        ...prevModels,
                        { id: `model_beam_${uuidv4()}`, type: "stl", scene: mesh },
                    ]);

                    URL.revokeObjectURL(url); // Clean up the object URL
                });
            
                break;

            case "SimpleTable":

                processTables(element, setTable)
                break;

            case "TablePlotHeatmapVideo":
                // Heatmap sequence / video (time frames)
                processHeatmapVideo(element, setPlots);
                break;
            case "TablePlotHeatmap":
            case "TablePlotLineal":
            case "TablePlotHistogram":
                // acá se debería tomar los datos del all_series_name y cargarlos de forma tal
                // que se puedan listar en los campos x_series_name e x_series_name.
            case "PowerMeter": 
                // data_series might be a JSON string or already an object depending on source
                processPlots(element, setPlots);

                break;
            
            default:
                // Statements to execute if no case matches
        }
    }
}


/**
 * Process the tables from different components and update the tables state
 * @param element 
 * @param setTables 
 * @returns 
 */
function processTables(element: any, setTables: React.Dispatch<React.SetStateAction<Map<string, any>>>){
    let data_series: any;
    try {
        data_series = typeof element.data === 'string' ? JSON.parse(element.data) : element.data;
    } catch (e) {
        console.error('Failed to parse Table data_series:', e, element.data);
        return;
    }

    const uniqueId = element.web_unique_id || (data_series && data_series.id);
    if (!uniqueId) {
        console.warn('Table element missing web_unique_id and data_series.id, skipping', element);
        return;
    }

    console.log(`Table update for id=${uniqueId}`, data_series);


    console.log('Incoming setTable:', setTables);
    // Update the plots Map: merge incoming traces/layout into the existing plot entry
    setTables((prev) => {
        const newTables = new Map(prev);
        const incoming = Array.isArray(data_series) ? data_series : [];
        console.log('Incoming table data:', incoming);
        // If incoming is empty, keep existing
        if (!incoming || incoming.length === 0) {
            return newTables;
        }

        // First row is expected to be columns
        const columnsRow = Array.isArray(incoming[0]) ? incoming[0] : [];
        const columns = columnsRow.map((c: any) => String(c));

        const rows: any[] = [];
        for (let r = 1; r < incoming.length; r++) {
            const rowArr = Array.isArray(incoming[r]) ? incoming[r] : [];
            const rowObj: any = {};
            // Index (1-based relative to first data row)
            rowObj.Index = r;

            for (let c = 0; c < columns.length; c++) {
                const colName = columns[c] || `col_${c}`;
                const raw = rowArr[c];
                if (typeof raw === 'string' && /^-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?$/.test(raw.trim())) {
                    rowObj[colName] = Number(raw);
                } else {
                    rowObj[colName] = raw;
                }
            }

            rows.push(rowObj);
        }

        const finalColumns = columns.includes('Index') ? columns : [ ...columns];

        const newTable = {
            id: uniqueId,
            rows: rows,
            columns: finalColumns,
        };
        console.log('Prepared newTable:', newTable);

        if (newTables.has(uniqueId)) {
            console.log(`Updating existing table with id=${uniqueId}`);
            const existing = newTables.get(uniqueId) || {};
            // Shallow merge: newer table replaces/overrides existing
            const updated = { ...(existing || {}), ...newTable };
            newTables.set(uniqueId, updated);
            console.log(`Table ${uniqueId} updated with ${rows.length} rows.`);
        } else {
            newTables.set(uniqueId, newTable);
            console.log(`Created new table ${uniqueId} with ${rows.length} rows.`);
        }

        return newTables;
    });
}



/**
 * Process the plots from different components and update the plots state
 * @param element 
 * @param setPlots 
 * @returns 
 */
function processPlots(element: any, setPlots: React.Dispatch<React.SetStateAction<Map<string, any>>>){
    let data_series: any;
    try {
        data_series = typeof element.data === 'string' ? JSON.parse(element.data) : element.data;
    } catch (e) {
        console.error('Failed to parse PowerMeter data_series:', e, element.data);
        return;
    }

    const uniqueId = element.web_unique_id || (data_series && data_series.id);
    if (!uniqueId) {
        console.warn('PowerMeter element missing web_unique_id and data_series.id, skipping', element);
        return;
    }

    console.log(`PowerMeter update for id=${uniqueId}`, data_series);

    // Update the plots Map: merge incoming traces/layout into the existing plot entry
    setPlots((prev) => {
        const newPlots = new Map(prev);
        const incoming = Array.isArray(data_series.data) ? data_series.data : [];

        if (newPlots.has(uniqueId)) {
            const existing = newPlots.get(uniqueId) || {};
            console.log(`Updating existing plot with id=${uniqueId}`);

            const existingData = Array.isArray(existing.data) ? existing.data : [];
            let mergedData: any[] = [];

            if (existingData.length > 0 && incoming.length > 0) {
                // Merge existing traces with incoming traces by index, incoming fields overwrite existing
                mergedData = existingData.map((trace: any, i: number) => {
                    const inc = incoming[i] || {};
                    return { ...trace, ...inc };
                });

                // Append any extra incoming traces
                if (incoming.length > existingData.length) {
                    mergedData.push(...incoming.slice(existingData.length).map((t: any) => ({ ...t })));
                }
            } else {
                // No existing traces: use incoming (could be empty)
                mergedData = incoming.map((t: any) => ({ ...t }));
            }

            const updated = { ...existing, data: mergedData };

            // Merge layout if provided
            if (data_series.layout) {
                updated.layout = { ...(existing.layout || {}), ...(data_series.layout || {}) };
            }

            newPlots.set(uniqueId, updated);
            console.log(`Plot ${uniqueId} updated.`);
        } else {
            // Create a new plot entry if it doesn't exist
            const newPlot = {
                id: uniqueId,
                data: incoming.map((t: any) => ({ ...t })),
                layout: data_series.layout || {},
            };
            newPlots.set(uniqueId, newPlot);
            console.log(`Created new plot ${uniqueId}.`);
        }

        return newPlots;
    });
}

/**
 * Process heatmap video / frames and update plots state
 * Expects `element.data` to contain an object with `frames` (array) or `data` (array of frames),
 * optional `currentFrame` and optional `layout`.
 */
function processHeatmapVideo(element: any, setPlots: React.Dispatch<React.SetStateAction<Map<string, any>>>){
    let data_series: any;
    try {
        data_series = typeof element.data === 'string' ? JSON.parse(element.data) : element.data;
    } catch (e) {
        console.error('Failed to parse HeatmapVideo data_series:', e, element.data);
        return;
    }

    const uniqueId = element.web_unique_id || (data_series && data_series.id);
    if (!uniqueId) {
        console.warn('HeatmapVideo element missing web_unique_id and data_series.id, skipping', element);
        return;
    }

    console.log(`HeatmapVideo update for id=${uniqueId}`, data_series);

    setPlots((prev) => {
        const newPlots = new Map(prev);

        const framesSrc = Array.isArray(data_series.frames) ? data_series.frames : (Array.isArray(data_series.data) ? data_series.data : []);
        const frames = framesSrc.map((f: any) => ({ ...f }));
        const currentFrame = typeof data_series.currentFrame === 'number' ? data_series.currentFrame : 0;

        const safeIndex = Math.max(0, Math.min(currentFrame, frames.length - 1));
        const initialFrame = frames.length > 0 ? frames[safeIndex] : (Array.isArray(data_series.data) && data_series.data.length > 0 ? data_series.data[0] : null);

        const newPlot = {
            id: uniqueId,
            frames: frames,
            currentFrame: safeIndex,
            data: initialFrame ? [initialFrame] : [],
            layout: data_series.layout || {},
        };

        // Replace or create the heatmap-video entry
        newPlots.set(uniqueId, newPlot);
        console.log(`Set heatmap video ${uniqueId} with ${frames.length} frames.`);

        return newPlots;
    });
}

function base64ToUtf8(base64: string): string {
  if (!base64) return "";
  try {
    if (typeof globalThis !== "undefined" && "Buffer" in globalThis) {
      // Node / environments with Buffer
      // @ts-ignore
      return Buffer.from(base64, "base64").toString("utf8");
    }
    // Browser
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return "";
  }
}




